import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getPhoneNumberPartsForNoCountryCode } from '../../../lib/utils';
import {
	getOldCountryCodeOptions,
	constructMobileNumber,
	getMobileTextFieldMaxLength,
	StyledInput,
	StyledSelect,
	Wrapper
} from '../../../commonComponents/fields/MobilePhoneField';
import FieldWrapper from './FieldWrapper';
import { createRules } from './utils';

// Note that this component has been duplicated from MobilePhoneField.tsx and needs to be kept the same.
// Unfortunately it is difficult to get the MobileNumber validation code to work correct with
// react-hook-forms and so it manually has to be called by the parent component in the 'global' validate
// callback.
const MobileNumber = (props: Props) => {
	const { fieldName, required } = props;
	const { getValues, control } = useFormContext();

	const initialMobileNumber = getPhoneNumberPartsForNoCountryCode(getValues(fieldName), props.customerCountryISOCode);
	const [countryCode, setCountryCode] = React.useState(initialMobileNumber.countryCode);
	const [nationalNumber, setNationalNumber] = React.useState(initialMobileNumber.nationalNumber);

	const handleCountryCodeChange =
		(onChange: (phoneNumber: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
			const selectedCountry = e.target.options[e.target.selectedIndex].value;
			setCountryCode(selectedCountry);
			onChange(constructMobileNumber(selectedCountry, nationalNumber, props.required));
		};

	const handleNationalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const digitsOnly = e.target.value.replace(/\D/g, '');
		setNationalNumber(digitsOnly);
	};

	const handleNationalNumberBlur =
		(onChange: (phoneNumber: string) => void) => (e: React.FocusEvent<HTMLInputElement>) => {
			const { value } = e.target;
			const newNationalNumber = value[0] === '0' ? value.slice(1) : value;
			onChange(constructMobileNumber(countryCode, newNationalNumber, props.required));
		};

	return (
		<FieldWrapper fieldName={fieldName} label="Mobile">
			<Controller
				name={fieldName}
				control={control}
				rules={{ ...createRules(required) }}
				defaultValue={getValues(fieldName)}
				render={(controllerProps) => (
					<>
						<Wrapper dataCaptureFieldStyling>
							<StyledSelect
								onChange={handleCountryCodeChange(controllerProps.field.onChange)}
								defaultValue={countryCode}
								disabled={props.disabled}
								dataCaptureFieldStyling
							>
								{getOldCountryCodeOptions().map((option) => (
									<option key={option.value} value={option.value}>
										{option.text}
									</option>
								))}
							</StyledSelect>
							<StyledInput
								name={`${fieldName}Text`}
								value={nationalNumber ?? ''}
								onChange={handleNationalNumberChange}
								onBlur={handleNationalNumberBlur(controllerProps.field.onChange)}
								maxLength={getMobileTextFieldMaxLength(countryCode)}
								pattern="[0-9]{9,13}"
								title="Please input valid mobile number"
								disabled={props.disabled}
								required={false}
								dataCaptureFieldStyling
							/>
						</Wrapper>
					</>
				)}
			/>
		</FieldWrapper>
	);
};

interface Props {
	fieldName: string;
	required?: boolean;
	disabled?: boolean;
	customerCountryISOCode?: string;
}

MobileNumber.defaultProps = {
	required: false,
	disabled: false,
	customerCountryISOCode: null
};

export default MobileNumber;
